import { useMemo } from 'react';
import { useCustomDataColumnsQuery } from 'api/useCustomDataColumnsQuery';
import services from 'services/services';
import {
    EMAIL_TEMPLATE_TYPE,
    MERGE_TAG_PANELIST_POINTS,
    MERGE_TAG_PANELIST_UUID,
    MERGE_TAG_SESSION_DURATION,
    MERGE_TAG_STUDY_INCENTIVE,
    MERGE_TAG_STUDY_LOCATION,
    MERGE_TAG_STUDY_SESSION,
    MERGE_TAG_STUDY_TITLE,
    MERGE_TAG_STUDYPANELIST_ID,
    MERGE_TAG_UNSUBSCRIBE_LINK,
    MERGE_TAG_UNSUBSCRIBE_LINK_LABEL
} from 'utils/constants';

const systemOptions = [
    { label: MERGE_TAG_PANELIST_UUID, value: MERGE_TAG_PANELIST_UUID },
    { label: MERGE_TAG_PANELIST_POINTS, value: MERGE_TAG_PANELIST_POINTS }
];

const studyOptions = [
    { label: MERGE_TAG_STUDYPANELIST_ID, value: MERGE_TAG_STUDYPANELIST_ID },
    { label: MERGE_TAG_STUDY_SESSION, value: MERGE_TAG_STUDY_SESSION },
    { label: MERGE_TAG_SESSION_DURATION, value: MERGE_TAG_SESSION_DURATION },
    { label: MERGE_TAG_STUDY_LOCATION, value: MERGE_TAG_STUDY_LOCATION },
    { label: MERGE_TAG_STUDY_INCENTIVE, value: MERGE_TAG_STUDY_INCENTIVE },
    { label: MERGE_TAG_STUDY_TITLE, value: MERGE_TAG_STUDY_TITLE }
];

const htmlOptions = [{ label: MERGE_TAG_UNSUBSCRIBE_LINK_LABEL, value: MERGE_TAG_UNSUBSCRIBE_LINK }];

/**
 * Shared hook for building merge tag options grouped into sections.
 *
 * Sections (in order):
 * 1. Panel Properties — custom data columns
 * 2. System Properties — PanelistUuid, PanelPoints
 * 3. Study Properties — study-related tags (optional)
 * 4. Custom Study Attributes — from study.custom_sp_attributes (optional)
 * 5. HTML Tags — unsubscribe link (only for HTML templates)
 *
 * @param {Object} params
 * @param {{ custom_sp_attributes: { title: string, id: string }[] }} [params.study]
 * @param {EMAIL_TEMPLATE_TYPE} [params.templateType]
 * @param {boolean} [params.includeStudyOptions=true]
 * @returns {{ isLoading: boolean, options: { label: string, value: string, disabled?: boolean }[] }}
 */
export function useMergeTagOptions({ study, templateType, includeStudyOptions = true } = {}) {
    const { data, isLoading } = useCustomDataColumnsQuery({
        onError: error => {
            services.parseAndTrackXhrErrors(error);
        }
    });

    const options = useMemo(() => {
        if (isLoading || !data) return [];

        const columnOptions = data.map(column => ({ label: column.title, value: column.title }));

        let result = [
            { label: 'Panel Properties', value: 'panel-properties-section', disabled: true },
            ...columnOptions,
            { label: 'System Properties', value: 'system-properties-section', disabled: true },
            ...systemOptions
        ];

        if (includeStudyOptions) {
            result = result.concat(
                { label: 'Study Properties', value: 'study-properties-section', disabled: true },
                ...studyOptions
            );
        }

        if (study && study.custom_sp_attributes && study.custom_sp_attributes.length) {
            result = result.concat(
                { label: 'Custom Study Attributes', value: 'custom-study-properties-section', disabled: true },
                ...study.custom_sp_attributes.map(attr => ({
                    label: attr.title,
                    value: `StudyAttribute:${attr.id}`
                }))
            );
        }

        if (templateType === EMAIL_TEMPLATE_TYPE.HTML) {
            result = result.concat(
                { label: 'HTML Tags', value: 'html-properties-section', disabled: true },
                ...htmlOptions
            );
        }

        return result;
    }, [data, isLoading, study, templateType, includeStudyOptions]);

    return { isLoading, options };
}
